import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, getFirestore, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import { useFirebase } from '../../contexts/firebaseContext';
import { BuildData, FirebaseError } from '../../types';

interface CustomGlobal {
    __app_id?: string;
}

const appId = (globalThis as unknown as CustomGlobal).__app_id || 'default-sworn-builder';

interface UseCommunityBuildsReturn {
    builds: BuildData[];
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

// Type guard for FirebaseError
const isFirebaseError = (error: unknown): error is FirebaseError => {
    return typeof error === 'object' && error !== null && 'code' in error && 'message' in error;
};

export const useCommunityBuilds = (): UseCommunityBuildsReturn => {
    const { app } = useFirebase();
    const [builds, setBuilds] = useState<BuildData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchBuilds = useCallback(async () => {
        if (!app) return;
        
        setLoading(true);
        setError(null);
        try {
            const db = getFirestore(app);
            const q = query(
                collection(db, 'artifacts', appId, 'shared_builds'),
                where('isCommunityBuild', '==', true),
                orderBy('updatedAt', 'desc'),
                limit(100)
            );
            const snapshot = await getDocs(q);

            const toIso = (value: unknown): string => {
                if (value && typeof value === 'object' && 'toDate' in value) {
                    return (value as Timestamp).toDate().toISOString();
                }
                if (typeof value === 'string') {
                    return value;
                }
                return new Date(0).toISOString();
            };
            
            const fetchedBuilds: BuildData[] = [];
            snapshot.forEach((docSnap) => {
                const data = docSnap.data() as BuildData;
                fetchedBuilds.push({
                    ...data,
                    id: docSnap.id,
                    createdAt: toIso(data.createdAt),
                    updatedAt: toIso(data.updatedAt),
                    sharedAt: toIso(data.sharedAt)
                });
            });
            
            // Sort locally (Newest updated first)
            fetchedBuilds.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
            
            setBuilds(fetchedBuilds);
        } catch (err: unknown) {
            console.error("Failed to fetch community builds", err);
            
            if (isFirebaseError(err)) {
                setError(`Error (${err.code}): ${err.message}`);
            } else if (err instanceof Error) {
                setError(err.message);
            } else {
                setError("Could not load community builds. Please try again later.");
            }
        } finally {
            setLoading(false);
        }
    }, [app]);

    // Initial fetch
    useEffect(() => {
        if (app) {
            fetchBuilds();
        }
    }, [app, fetchBuilds]);

    return { builds, loading, error, refresh: fetchBuilds };
};
